import { useCallback, useEffect, useMemo, useState } from "react";

import debounce from "lodash.debounce";
import { Column, IconButton, TagTextAlert, Row, DialogWrapper, Span, Text, Tooltip } from "@bridgesplit/ui";
import {
    useLoopscaleWalletContext,
    usePandoraQuoteAndIxs,
    getPandoraErrorMessage,
    useTokenSwapTransaction,
    TokenListMetadata,
    useTokenListContext,
    useOraclePrices
} from "@bridgesplit/abf-react";
import { SwapVert } from "@mui/icons-material";
import {
    AppButton,
    DEFAULT_SLIPPAGE,
    DEFAULT_SLIPPAGE_PRESETS,
    DialogHeaderWithBack,
    Slippage,
    formatSlippageRowEndText
} from "app/components/common";
import {
    decimalsToBps,
    uiAmountToLamports,
    lamportsToUiAmount,
    Result,
    MISSING_PARAM_ERROR,
    bsMath,
    formatNum,
    minInputInToken
} from "@bridgesplit/utils";
import { SwapInstructionsResponse, SwapParams, RouteStepType } from "@loopscale/pandora-types";
import { TransactionResult } from "@bridgesplit/react";
import { AppDialog, useAppDialog } from "app/utils";

import { useBalanceChecker, BalanceDisplay } from "../common";
import { TokenInputWithPrice } from "../common/util";
import { SwapFormState, SwapType } from "./types";
import { useSignAndSendTransactionSender } from "../transactions/util";
import { ActionsSettingsButton } from "../loan/actions/common";
import { AsyncSwapSettlement } from "./AsyncSwapSettlement";
import { getActualBuyTokenFromSwapQuote, isSwapQuoteForForm } from "./util";
import { useSwapPayWithView } from "./useSwapPayWithView";
import { ExecutionDetails, ExecutionDetailsLabels, useExecutionDetails } from "../loop/execution-detail";

export function SwapDialog() {
    const { getData, close } = useAppDialog();
    const data = getData(AppDialog.Swap);

    return (
        <DialogWrapper>
            <Swap buyToken={data?.buyToken} sellToken={data?.sellToken} onClose={close} />
        </DialogWrapper>
    );
}

export function Swap({
    buyToken,
    sellToken,
    onClose,
    inNestedDialogView = false
}: {
    buyToken: TokenListMetadata | undefined;
    sellToken: TokenListMetadata | undefined;
    onClose?: () => void;
    inNestedDialogView?: boolean;
}) {
    const { slippageWrapperProps, slippageButtonProps, slippagePercentUi, slippagePercentDecimals } =
        Slippage.useController({
            defaultSlippageUi: DEFAULT_SLIPPAGE
        });
    const [form, setForm] = useState<SwapFormState>({
        sellToken: sellToken,
        buyToken: buyToken,
        inputAmount: undefined,
        swapType: SwapType.Swap
    });
    const [inputAmount, setInputAmount] = useState<number | undefined>(undefined);

    // Async swap settlement tracking. Tokens are captured at success time so the settlement view
    // (and the two-step continue CTA) can't drift if the quote refetches while polling.
    const [settlementParams, setSettlementParams] = useState<{
        redemptionId: string;
        routeType: string;
        deliveredToken?: TokenListMetadata;
        finalBuyToken?: TokenListMetadata;
        // GLAM-style redeems settle to a pending request; the user must sign a second tx (claim)
        // to actually receive their output. Undefined for self-settling flows (Fission etc.).
        claimParams?: SwapParams;
        // Route-specific poll param (e.g. GLAM vault state PDA).
        pollState?: string;
    } | null>(null);

    const {
        data: { userWalletAddress: userWallet }
    } = useLoopscaleWalletContext();

    const { getMetadata } = useTokenListContext();

    // Once the user explicitly picks/flips the sell token, stop auto-selecting a pay-with token.
    const [sellTokenOverridden, setSellTokenOverridden] = useState(false);

    const {
        payWithTokens,
        effectiveSellToken,
        effectiveForm,
        routeIntermediateSymbol,
        routeAsyncPossible,
        flipDisabled,
        routeMinInput
    } = useSwapPayWithView({ form, sellTokenOverridden });

    const {
        balance: sellBalance,
        insufficientBalance,
        isLoading: sellBalanceLoading
    } = useBalanceChecker({
        mint: effectiveSellToken?.mint,
        amount: inputAmount ?? form.inputAmount,
        skip: false
    });

    const { swapQuote, isQuoteLoading, swapQuoteError } = useSwapQuote(
        effectiveForm,
        slippagePercentDecimals,
        userWallet
    );

    const quoteMatchesForm = useMemo(
        () => isSwapQuoteForForm(swapQuote, effectiveSellToken, form.inputAmount),
        [swapQuote, effectiveSellToken, form.inputAmount]
    );

    // Get the actual output token from the quote (handles outputMintOverride for fission swaps)
    const actualBuyToken = useMemo(
        () => getActualBuyTokenFromSwapQuote(swapQuote, form.buyToken, getMetadata),
        [swapQuote, form.buyToken, getMetadata]
    );

    /** Buy row token: don't derive from a stale quote after a token flip—show form.buyToken until the new quote lands. */
    const displayBuyToken = quoteMatchesForm ? actualBuyToken : form.buyToken;

    // Check if this is an async swap (e.g., Fission); quote.async catches async steps beyond the first.
    const firstRouteStep = swapQuote?.routeSteps?.[0];
    const isAsyncSwap = quoteMatchesForm && (firstRouteStep?.async === true || swapQuote?.quote?.async === true);

    // Async quote that delivers an intermediate token (quote output-mint override): the user must
    // run a second swap after settlement to reach the requested buy token.
    const asyncIntermediateToken =
        isAsyncSwap && actualBuyToken && form.buyToken && actualBuyToken.mint !== form.buyToken.mint
            ? actualBuyToken
            : undefined;

    // Track all mints that can appear so oracles stay in sync with current form state.
    const mints = useMemo(
        () => [effectiveSellToken?.mint, form.buyToken?.mint, actualBuyToken?.mint],
        [effectiveSellToken?.mint, form.buyToken?.mint, actualBuyToken?.mint]
    );

    const { getOracle } = useOraclePrices(mints);

    // Use current form sell token and the token shown on the buy row for oracle prices.
    const sellTokenOracle = getOracle(effectiveSellToken?.mint);
    const actualBuyTokenOracle = getOracle(actualBuyToken?.mint);

    const { balance: buyBalance, isLoading: buyBalanceLoading } = useBalanceChecker({
        mint: displayBuyToken?.mint,
        amount: undefined,
        skip: false
    });

    const ctaError = useMemo(() => {
        if (!userWallet) return "No wallet connected";
        if (insufficientBalance) return "Insufficient balance";
        // Route step minimum (e.g. Glow's 10 USD.tel deposit min) is enforced in the step's own
        // token; comparing the sell side in USD is an approximation — on-chain is the backstop.
        if (
            routeMinInput &&
            form.inputAmount &&
            sellTokenOracle?.usdPrice &&
            form.inputAmount * sellTokenOracle.usdPrice < routeMinInput.minUsd
        ) {
            // Say it in the sell token: how much USDG buys the 10 USD.tel step minimum.
            const minSellAmount = minInputInToken(routeMinInput.minUsd, sellTokenOracle.usdPrice);
            return `Minimum swap is ${formatNum(minSellAmount)} ${effectiveSellToken?.symbol ?? routeMinInput.symbol}`;
        }
    }, [insufficientBalance, userWallet, routeMinInput, form.inputAmount, sellTokenOracle, effectiveSellToken]);

    const alertError = useMemo(() => {
        if (swapQuoteError)
            return getPandoraErrorMessage(
                swapQuoteError,
                "Unable to find a swap route through our integration partners."
            );
    }, [swapQuoteError]);

    const oracleWarning = useMemo(() => {
        // Check if oracle prices are missing for sell or buy tokens
        if (effectiveSellToken && !sellTokenOracle && form.inputAmount) {
            return `Unable to calculate execution details because oracle price not found for ${effectiveSellToken.symbol}. Please verify simulated swap amount.`;
        }
        if (actualBuyToken && !actualBuyTokenOracle && form.inputAmount) {
            return `Unable to calculate execution details because oracle price not found for ${actualBuyToken.symbol}. Please verify simulated swap amount.`;
        }
        return undefined;
    }, [effectiveSellToken, actualBuyToken, sellTokenOracle, actualBuyTokenOracle, form.inputAmount]);

    const buyAmount =
        quoteMatchesForm && !isQuoteLoading && !swapQuoteError && form.inputAmount && swapQuote?.quote?.minOutputAmount
            ? lamportsToUiAmount(swapQuote.quote.minOutputAmount, actualBuyToken?.decimals)
            : undefined;

    const handleAmountChange = useCallback(
        (amount: number | undefined) => {
            setForm((prev) => {
                if (prev.inputAmount === amount) return prev;
                return {
                    ...prev,
                    inputAmount: amount
                };
            });
        },
        [setForm]
    );

    const setValueDebounced = useMemo(() => debounce(handleAmountChange, 500), [handleAmountChange]);

    useEffect(() => {
        return () => setValueDebounced.cancel();
    }, [setValueDebounced]);

    const isDebouncing = inputAmount !== form.inputAmount;
    const buyLoading =
        !!inputAmount && (isQuoteLoading || isDebouncing || (!!form.inputAmount && !swapQuoteError && !actualBuyToken));

    const handleToggleTokens = useCallback(() => {
        // Prevent stale debounced updates from re-applying the previous amount after a toggle.
        setValueDebounced.cancel();

        // Toggle should always be deterministic: just swap the currently selected tokens.
        const nextSellToken = form.buyToken;
        const nextBuyToken = effectiveSellToken;
        // An explicit flip pins the sell token; stop pay-with auto-selection.
        setSellTokenOverridden(true);

        const hasOutputMintOverride =
            !!actualBuyToken?.mint && !!form.buyToken?.mint && actualBuyToken.mint !== form.buyToken.mint;

        const shouldCarryAmount =
            !isQuoteLoading &&
            !isDebouncing &&
            !swapQuoteError &&
            !!actualBuyToken &&
            !hasOutputMintOverride &&
            buyAmount != null;

        const newInputAmount = shouldCarryAmount ? buyAmount : undefined;

        setForm({
            ...form,
            sellToken: nextSellToken,
            buyToken: nextBuyToken,
            inputAmount: newInputAmount,
            swapType: SwapType.Swap // Plume and other tokens will have mint steps
        });
        setInputAmount(newInputAmount);
    }, [
        buyAmount,
        form,
        effectiveSellToken,
        isDebouncing,
        isQuoteLoading,
        actualBuyToken,
        setForm,
        setValueDebounced,
        swapQuoteError
    ]);

    const handleSwapSuccess = useCallback(() => {
        const routeStep = swapQuote?.routeSteps?.[0];

        // Check if this is an async swap that needs settlement tracking
        if (isAsyncSwap && routeStep?.transactionIdentifier) {
            const redemptionId = routeStep.transactionIdentifier;
            const routeType = routeStep.exchangeName || RouteStepType.FISSION;

            // GLAM redeems require a second POST /swap-instructions (echoing the transactionIdentifier)
            // to fetch the claim ix. Detected via the discriminated route step: type=GLAM + action=redeem.
            const isGlamRedeem =
                routeStep.type === RouteStepType.GLAM &&
                "action" in routeStep &&
                routeStep.action === "redeem";
            const claimParams: SwapParams | undefined =
                isGlamRedeem && swapQuote?.quote && userWallet
                    ? {
                          slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
                          userWallet,
                          inputMint: swapQuote.quote.inputMint,
                          outputMint: swapQuote.quote.outputMint,
                          inputAmount: swapQuote.quote.inputAmount,
                          optionalParams: {
                              useJito: false,
                              includeBase64Transaction: true,
                              routeType: RouteStepType.GLAM,
                              transactionIdentifier: redemptionId
                          }
                      }
                    : undefined;

            // GLAM polling requires the vault's state PDA; pulled from the route step (GlamRouteParams).
            const pollState =
                routeStep.type === RouteStepType.GLAM && "state" in routeStep ? routeStep.state : undefined;

            setSettlementParams({
                redemptionId,
                routeType,
                deliveredToken: actualBuyToken,
                // Two-step purchase: the async leg delivers an intermediate; a second swap finishes.
                finalBuyToken: asyncIntermediateToken ? form.buyToken : undefined,
                claimParams,
                pollState
            });
        } else {
            // Normal swap - close immediately
            onClose?.();
        }
    }, [
        isAsyncSwap,
        swapQuote,
        onClose,
        actualBuyToken,
        asyncIntermediateToken,
        form.buyToken,
        slippagePercentDecimals,
        userWallet
    ]);

    const executeSwap = useExecuteSwap(swapQuote, userWallet, handleSwapSuccess);

    // Second leg of a two-step purchase: prefill selling the delivered intermediate for the original target.
    const handleContinueToFinalSwap = useCallback(() => {
        if (!settlementParams?.deliveredToken || !settlementParams.finalBuyToken) return;
        setSellTokenOverridden(true); // explicit selection — keep pay-with auto-selection off
        setForm({
            sellToken: settlementParams.deliveredToken,
            buyToken: settlementParams.finalBuyToken,
            inputAmount: undefined,
            swapType: SwapType.Swap
        });
        setInputAmount(undefined);
        setSettlementParams(null);
    }, [settlementParams]);

    const receiveAmountUsd = useMemo(() => {
        if (!quoteMatchesForm || isQuoteLoading || !swapQuote?.quote?.minOutputAmount) return undefined;
        return bsMath.mul(
            lamportsToUiAmount(swapQuote.quote.minOutputAmount, actualBuyToken?.decimals),
            actualBuyTokenOracle?.usdPrice ?? 0
        );
    }, [quoteMatchesForm, isQuoteLoading, swapQuote, actualBuyToken, actualBuyTokenOracle?.usdPrice]);

    const sellAmountUsd = useMemo(() => {
        if (!quoteMatchesForm || isQuoteLoading || !swapQuote?.quote?.inputAmount) return undefined;
        return bsMath.mul(
            lamportsToUiAmount(swapQuote.quote.inputAmount, effectiveSellToken?.decimals),
            sellTokenOracle?.usdPrice
        );
    }, [quoteMatchesForm, isQuoteLoading, swapQuote, effectiveSellToken?.decimals, sellTokenOracle?.usdPrice]);

    const executionDetails = useExecutionDetails({
        slippagePercentDecimals,
        externalQuote: quoteMatchesForm && !isQuoteLoading ? swapQuote : undefined,
        receiveUsdAmount: receiveAmountUsd,
        fairMarketValue: sellAmountUsd,
        labelOverrides: SWAP_EXECUTION_DETAILS_LABELS,
        principalUsdPrice: sellTokenOracle?.usdPrice,
        collateralUsdPrice: quoteMatchesForm ? actualBuyTokenOracle?.usdPrice : undefined,
        principalMetadata: effectiveSellToken,
        collateralMetadata: quoteMatchesForm ? actualBuyToken : form.buyToken
    });

    // Show async settlement view when tracking settlement
    if (settlementParams) {
        return (
            <AsyncSwapSettlement
                redemptionId={settlementParams.redemptionId}
                routeType={settlementParams.routeType}
                buyToken={settlementParams.deliveredToken ?? actualBuyToken}
                finalBuyToken={settlementParams.finalBuyToken}
                onContinue={settlementParams.finalBuyToken ? handleContinueToFinalSwap : undefined}
                onClose={onClose}
                inNestedDialogView={inNestedDialogView}
                claimParams={settlementParams.claimParams}
                pollState={settlementParams.pollState}
            />
        );
    }

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            <Column spacing={3}>
                <Row spaceBetween>
                    {inNestedDialogView ? (
                        <DialogHeaderWithBack back={onClose} header="Swap" />
                    ) : (
                        <Text variant="h3">Swap</Text>
                    )}

                    <ActionsSettingsButton
                        rowEndText={formatSlippageRowEndText(slippagePercentUi, slippagePercentDecimals)}
                        onClick={slippageButtonProps.open}
                    />
                </Row>
                <Column>
                    <TokenInputWithPrice
                        label="Sell"
                        value={inputAmount}
                        setValue={(value) => {
                            setInputAmount(value);
                            setValueDebounced(value);
                        }}
                        metadata={effectiveSellToken}
                        selectToken={
                            payWithTokens && payWithTokens.length > 1
                                ? {
                                      tokens: payWithTokens,
                                      setToken: (token) => {
                                          setSellTokenOverridden(true);
                                          setForm((prev) => ({ ...prev, sellToken: token }));
                                      },
                                      header: "Pay with",
                                      showBalances: true
                                  }
                                : null
                        }
                        supportedDepositTokens={payWithTokens && payWithTokens.length > 1 ? payWithTokens : undefined}
                        labelColor="caption"
                        belowInput={
                            <BalanceDisplay
                                isLoading={sellBalanceLoading}
                                balance={sellBalance}
                                metadata={effectiveSellToken}
                                setMax={(value) => {
                                    setInputAmount(value);
                                    handleAmountChange(value);
                                }}
                                insufficientBalance={insufficientBalance}
                                hideRefresh
                            />
                        }
                        tokenImageSize="md"
                    />

                    <Column alignItems="center">
                        <Row>
                            <Tooltip
                                title={
                                    flipDisabled
                                        ? "Withdrawals from this vault are epoch-based and can't be swapped atomically"
                                        : ""
                                }
                            >
                                <Span>
                                    <IconButton
                                        onClick={handleToggleTokens}
                                        disabled={flipDisabled}
                                        color="secondary"
                                        jsxIcon={<SwapVert />}
                                        sx={{
                                            border: ({ palette }) => `1px solid ${palette.divider}`,
                                            ":hover": {
                                                border: ({ palette }) => `1px solid ${palette.divider}`
                                            }
                                        }}
                                    />
                                </Span>
                            </Tooltip>
                        </Row>
                    </Column>
                    {/* Buy Section */}
                    <TokenInputWithPrice
                        label={"Buy"}
                        value={buyAmount}
                        setValue={() => undefined}
                        metadata={displayBuyToken}
                        selectToken={null}
                        disabled={true}
                        loading={buyLoading}
                        labelColor="caption"
                        belowInput={
                            <Row spaceBetween>
                                <BalanceDisplay
                                    isLoading={buyBalanceLoading}
                                    balance={buyBalance}
                                    metadata={displayBuyToken}
                                    insufficientBalance={false}
                                    hideRowEnd
                                    hideRefresh
                                />
                            </Row>
                        }
                        tokenImageSize="md"
                    />
                </Column>

                {alertError && (
                    <TagTextAlert color="warning" icon="warning">
                        {alertError}
                    </TagTextAlert>
                )}

                {oracleWarning && !swapQuoteError && !isQuoteLoading && (
                    <TagTextAlert color="error" icon="warning">
                        {oracleWarning}
                    </TagTextAlert>
                )}

                {routeIntermediateSymbol && !swapQuoteError && (
                    <TagTextAlert color="info" icon="tooltip">
                        Swaps via {routeIntermediateSymbol}.
                        {routeAsyncPossible && " The swap may take a few minutes if no instant liquidity is available."}
                    </TagTextAlert>
                )}

                {isAsyncSwap && !swapQuoteError && (
                    <TagTextAlert color="info" icon="tooltip">
                        {asyncIntermediateToken && form.buyToken
                            ? `You'll receive ${asyncIntermediateToken.symbol} first (settlement takes approximately 3-5 minutes). Once it arrives, swap ${asyncIntermediateToken.symbol} for ${form.buyToken.symbol} to finish.`
                            : "Settlement will take approximately 3-5 minutes after the transaction confirms."}
                    </TagTextAlert>
                )}

                {swapQuote && executionDetails && !swapQuoteError && (
                    <ExecutionDetails
                        executionDetails={executionDetails}
                        loading={isQuoteLoading}
                        textVariant="body2"
                        slippagePresetsUi={DEFAULT_SLIPPAGE_PRESETS}
                    />
                )}

                <AppButton
                    fullWidth
                    size="large"
                    asyncCta={{ onClickWithResult: executeSwap }}
                    isTransaction
                    disabled={
                        !!alertError ||
                        !!ctaError ||
                        !form.inputAmount ||
                        !quoteMatchesForm ||
                        !swapQuote ||
                        !buyAmount ||
                        !sellBalance ||
                        buyLoading
                    }
                >
                    {ctaError || "Swap"}
                </AppButton>
            </Column>
        </Slippage.Wrapper>
    );
}

function useExecuteSwap(
    swapQuote: SwapInstructionsResponse | undefined,
    userWallet: string | undefined,
    onClose?: () => void
) {
    const send = useSignAndSendTransactionSender();
    const swapTransactionDetails = useTokenSwapTransaction();

    return useCallback(async (): Promise<Result<TransactionResult[]>> => {
        if (!swapQuote || !userWallet) {
            return Result.errFromMessage(MISSING_PARAM_ERROR);
        }
        return send(
            swapTransactionDetails,
            { swapQuote, userWallet },
            {
                messageOverrides: {
                    onSuccess: "Swap complete",
                    onError: "Swap failed to confirm"
                },
                sendOptions: { onSuccess: () => onClose?.() },
                preventAutoDialogClose: true
            }
        );
    }, [swapQuote, userWallet, onClose, send, swapTransactionDetails]);
}

function useSwapQuote(
    form: SwapFormState,
    slippagePercentDecimals: number | undefined,
    userWallet: string | undefined
): {
    swapQuote: SwapInstructionsResponse | undefined;
    isQuoteLoading: boolean;
    swapQuoteError: unknown;
} {
    const request: SwapParams | undefined = useMemo(() => {
        if (!form.inputAmount || !userWallet || !form.sellToken || !form.buyToken) return undefined;

        return {
            slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
            userWallet: userWallet,
            inputMint: form.sellToken.mint,
            outputMint: form.buyToken.mint,
            inputAmount: uiAmountToLamports(form.inputAmount, form.sellToken.decimals),
            optionalParams: { useJito: false, includeBase64Transaction: true }
        };
    }, [slippagePercentDecimals, userWallet, form.inputAmount, form.sellToken, form.buyToken]);

    const { data: quote, isLoading, isFetching, error } = usePandoraQuoteAndIxs(request);

    const swapQuote = useMemo(() => {
        if (!request || !quote?.quote) return undefined;
        // Only return quotes that match the current request (prevents stale quotes after toggle/input changes).
        if (quote.quote.inputMint !== request.inputMint) return undefined;
        if (quote.quote.inputAmount !== request.inputAmount) return undefined;
        return quote;
    }, [quote, request]);

    const isQuoteLoading = isLoading || isFetching;

    return { swapQuote, isQuoteLoading, swapQuoteError: error };
}

const SWAP_EXECUTION_DETAILS_LABELS: Partial<ExecutionDetailsLabels> = {
    executionImpact: {
        caption: "Execution impact",
        tooltip: "Combined worst-case impact of slippage, fees, price impact, and market price on your swap."
    },
    fairMarketValue: {
        caption: "",
        tooltip: undefined,
        hide: true
    },
    oracleSpread: {
        caption: "Oracle spread",
        tooltip: "Difference between the oracle-estimated and market price."
    },
    receiveOnClose: {
        caption: "",
        tooltip: undefined,
        hide: true
    }
};
